/**
 * Lightweight confetti effect rendered on a transient HTML5 canvas overlay.
 *
 * Why a custom implementation: avoids adding a runtime dependency
 * (e.g. canvas-confetti) and works without reinstalling node_modules
 * or restarting the dev server.
 */

interface ConfettiBurstOptions {
  particleCount?: number;
  originX?: number;
  originY?: number;
  spread?: number;
  startVelocity?: number;
  colors?: string[];
}

interface ConfettiParticle {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  size: number;
  rotation: number;
  rotationSpeed: number;
  color: string;
  opacity: number;
}

const DEFAULT_COLORS: string[] = [
  '#1B3A57',
  '#3DB5A4',
  '#F4B860',
  '#E2654A',
  '#9B5DE5',
  '#00BBF9',
  '#F15BB5',
];

const GRAVITY = 0.18;
const DRAG = 0.012;
const FADE_RATE = 0.012;
const PARTICLE_LIFETIME_FRAMES = 220;

const randomBetween = (min: number, max: number): number =>
  min + Math.random() * (max - min);

const pickRandomColor = (palette: string[]): string =>
  palette[Math.floor(Math.random() * palette.length)];

const createOverlayCanvas = (): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.style.position = 'fixed';
  canvas.style.inset = '0';
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.pointerEvents = 'none';
  canvas.style.zIndex = '9999';
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  document.body.appendChild(canvas);
  return canvas;
};

const createParticles = (
  options: Required<ConfettiBurstOptions>
): ConfettiParticle[] => {
  const {
    particleCount,
    originX,
    originY,
    spread,
    startVelocity,
    colors,
  } = options;

  const particles: ConfettiParticle[] = [];
  for (let index = 0; index < particleCount; index += 1) {
    const angle = randomBetween(-spread / 2, spread / 2) * (Math.PI / 180);
    const velocity = startVelocity * randomBetween(0.6, 1);
    particles.push({
      x: originX * window.innerWidth,
      y: originY * window.innerHeight,
      velocityX: Math.sin(angle) * velocity,
      velocityY: -Math.cos(angle) * velocity,
      size: randomBetween(6, 10),
      rotation: randomBetween(0, Math.PI * 2),
      rotationSpeed: randomBetween(-0.25, 0.25),
      color: pickRandomColor(colors),
      opacity: 1,
    });
  }
  return particles;
};

const renderParticles = (
  context: CanvasRenderingContext2D,
  particles: ConfettiParticle[]
): void => {
  particles.forEach(particle => {
    context.save();
    context.globalAlpha = Math.max(0, particle.opacity);
    context.translate(particle.x, particle.y);
    context.rotate(particle.rotation);
    context.fillStyle = particle.color;
    context.fillRect(
      -particle.size / 2,
      -particle.size / 2,
      particle.size,
      particle.size
    );
    context.restore();
  });
};

const updateParticle = (particle: ConfettiParticle): void => {
  particle.velocityY += GRAVITY;
  particle.velocityX *= 1 - DRAG;
  particle.velocityY *= 1 - DRAG;
  particle.x += particle.velocityX;
  particle.y += particle.velocityY;
  particle.rotation += particle.rotationSpeed;
  particle.opacity -= FADE_RATE;
};

const fireConfettiBurst = (options: ConfettiBurstOptions = {}): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const resolvedOptions: Required<ConfettiBurstOptions> = {
    particleCount: options.particleCount ?? 120,
    originX: options.originX ?? 0.5,
    originY: options.originY ?? 0.65,
    spread: options.spread ?? 90,
    startVelocity: options.startVelocity ?? 18,
    colors: options.colors ?? DEFAULT_COLORS,
  };

  const canvas = createOverlayCanvas();
  const context = canvas.getContext('2d');
  if (!context) {
    document.body.removeChild(canvas);
    return;
  }

  const particles = createParticles(resolvedOptions);
  let framesElapsed = 0;

  const animate = (): void => {
    framesElapsed += 1;
    context.clearRect(0, 0, canvas.width, canvas.height);

    particles.forEach(updateParticle);
    renderParticles(context, particles);

    const stillVisible = particles.some(
      particle => particle.opacity > 0 && particle.y < canvas.height + 50
    );

    if (stillVisible && framesElapsed < PARTICLE_LIFETIME_FRAMES) {
      window.requestAnimationFrame(animate);
      return;
    }

    if (canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
    }
  };

  window.requestAnimationFrame(animate);
};

export const fireSuccessConfetti = (): void => {
  fireConfettiBurst({
    particleCount: 160,
    originX: 0.5,
    originY: 0.6,
    spread: 100,
    startVelocity: 22,
  });

  window.setTimeout(() => {
    fireConfettiBurst({
      particleCount: 90,
      originX: 0.05,
      originY: 0.7,
      spread: 70,
      startVelocity: 24,
    });
  }, 220);

  window.setTimeout(() => {
    fireConfettiBurst({
      particleCount: 90,
      originX: 0.95,
      originY: 0.7,
      spread: 70,
      startVelocity: 24,
    });
  }, 420);
};
